import moment from 'moment';
import { convertTime, machineStatuses, subStatuses, subStatusName } from '@components/wallboard/components/functions';
import { getTimelineLineHeight } from '@components/wallboard/helpers/productivityTimeline';
import type { StatusIcon } from '@components/wallboard/types';

export interface UseManageNotProductiveTileParams {
  timeOffset: number;
  formatSinceTime: (date: string, is24Hour: boolean) => string;
  ncFileStatuses: Record<string, string>;
  machineStatus: {
    ncFileStatus: string | null;
    ncFile: string | null;
    subStatus: string | null;
    status: string;
    lastUpdatedOn: string;
    isConnected: boolean;
  };
  is24HourFormat: boolean;
  tileHeight: number;
}

export interface UseManageNotProductiveTileReturn {
  lineHeight: number;
  icon: StatusIcon | null;
  title: string;
  ncFile: string | null;
  runningTitle: string | null;
  runningTime: string;
  showSinceLabel: boolean;
  isIgnoreStatus: boolean;
  isSetupStatus: boolean;
  isNoNCFileStatus: boolean;
}

const SUB_STATUS_TITLE_MAP: Record<string, { title: string; icon: StatusIcon }> = {
  [subStatuses.NotPingable]: { title: 'Machine off', icon: { name: 'machineOff', width: 47, height: 25 } },
  [subStatuses.Offline]: { title: 'Machine offline', icon: { name: 'machineOffline', width: 54, height: 36 } },
  [subStatuses.ConnectorOff]: { title: 'Connector offline', icon: { name: 'connectorOfflineLarge', width: 54, height: 36 } },
  [subStatuses.ConnectionFailed]: {
    title: 'No connection possible',
    icon: { name: 'noConnectionPossible', width: 23, height: 27 },
  },
  [subStatuses.MachineConnectorOff]: {
    title: 'Connection Switched Off',
    icon: { name: 'machineConnectionOff', width: 54, height: 36 },
  },
  [subStatuses.NoConnector]: {
    title: 'No connector',
    icon: { name: 'noConnectorLarge', width: 54, height: 36, color: 'Label/Light/Error' },
  },
};

const useManageNotProductiveTile = ({
  timeOffset,
  formatSinceTime,
  ncFileStatuses,
  machineStatus,
  is24HourFormat,
  tileHeight,
}: UseManageNotProductiveTileParams): UseManageNotProductiveTileReturn => {
  const { ncFileStatus, ncFile, subStatus, status, lastUpdatedOn, isConnected } = machineStatus;

  const lineHeight = getTimelineLineHeight(tileHeight);

  const isNcFileNull = !ncFileStatus;
  const isNcFileStatusIgnored = ncFileStatus === ncFileStatuses.Ignored;
  const isNcFileStatusSetup = ncFileStatus === ncFileStatuses.Setup;
  const isNoNCFileStatus = isNcFileNull && isConnected;

  const isNotProductive = status === machineStatuses.NOTPRODUCTIVE;
  const isActiveStatus = status === machineStatuses.ACTIVE;
  const isIgnoreStatus = isNcFileStatusIgnored && isConnected;
  const isSetupStatus = isNcFileStatusSetup && isConnected;

  const isSubStatus = !isConnected && isNcFileNull && Boolean(subStatus);
  const isSubStatusRunning = subStatus === subStatuses.Running;

  const isMoreHour = moment.utc().diff(moment.utc(lastUpdatedOn), 'm') >= 60;

  const getMachineStatusDate = (): { title: string; icon: StatusIcon | null } => {
    if (isIgnoreStatus) {
      return { title: 'Ignored NC File', icon: { name: 'machineIgnoredSmall', width: 32, height: 26 } };
    }

    if (isSetupStatus) {
      return { title: 'Setup NC File', icon: { name: 'setup', width: 32, height: 26 } };
    }

    if (isNoNCFileStatus) {
      return { title: 'No NC File', icon: null };
    }

    if (isSubStatus) {
      return SUB_STATUS_TITLE_MAP[subStatus!] || { title: subStatusName[subStatus!], icon: { name: 'machineOffline', width: 54, height: 36 } };
    }

    return { title: 'Connecting...', icon: null };
  };

  const getRunningLabel = (): { runningTitle: string | null; runningTime: string; showSinceLabel: boolean } => {
    const time = moment.utc().add(timeOffset, 'milliseconds');
    const lastModifiedMilliseconds = moment.utc(lastUpdatedOn).valueOf();
    const lastTime = Math.round((time.utc().valueOf() - lastModifiedMilliseconds) / 1000);
    const formattedSinceDate = formatSinceTime(lastUpdatedOn, is24HourFormat);
    const formattedTime = convertTime(lastTime);

    if (isSubStatusRunning && isNotProductive && isConnected) {
      return { runningTitle: 'Running Not Productive', runningTime: formattedSinceDate, showSinceLabel: true };
    }

    if (!isSubStatusRunning && isNotProductive && isConnected) {
      const currentTimeFormat = isMoreHour ? formattedSinceDate : formattedTime;
      return { runningTitle: 'Not Running', runningTime: currentTimeFormat, showSinceLabel: isMoreHour };
    }

    if (isSubStatusRunning && isActiveStatus && isConnected) {
      return { runningTitle: 'Running', runningTime: formattedTime, showSinceLabel: false };
    }

    return { runningTitle: null, runningTime: formattedSinceDate, showSinceLabel: true };
  };

  const { icon, title } = getMachineStatusDate();
  const { runningTime, runningTitle, showSinceLabel } = getRunningLabel();

  return {
    lineHeight,
    icon,
    title,
    ncFile,
    runningTitle,
    runningTime,
    showSinceLabel,
    isIgnoreStatus,
    isSetupStatus,
    isNoNCFileStatus,
  };
};

export default useManageNotProductiveTile;
